import { RELATIVE_TODAY } from "./constants";
import { getPath, isNil } from "./utils";

const isEmptyValue = (value) => {
  if (isNil(value) || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
};

const pad2 = (value) => String(value).padStart(2, "0");

const formatLocalDate = (date) =>
  [date.getFullYear(), pad2(date.getMonth() + 1), pad2(date.getDate())].join(
    "-",
  );

// Date-only values from <input type="date"> are calendar days. ISO datetimes
// from the API are instants — convert them to the local calendar day so
// "today" matches what the date picker shows (e.g. 22:00Z = next morning in
// France).
export const toCalendarDateKey = (value) => {
  if (typeof value !== "string") return value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (!/^\d{4}-\d{2}-\d{2}[T\s]/.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);
  return formatLocalDate(parsed);
};

export const resolveRightOperand = (rightOperand, now = new Date()) => {
  const value = rightOperand && rightOperand.value;
  if (value !== RELATIVE_TODAY) return value;
  const offsetDays =
    rightOperand && !isNil(rightOperand.offsetDays)
      ? Number(rightOperand.offsetDays)
      : 0;
  const date = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + offsetDays,
  );
  return formatLocalDate(date);
};

const leftValue = (condition, context) =>
  getPath(context, getPath(condition, "leftOperand.accessor"));

const includesValue = (left, right) => {
  if (Array.isArray(left)) {
    return left.map((item) => String(item)).indexOf(String(right)) >= 0;
  }
  if (isNil(left)) return false;
  return String(left).indexOf(String(right)) >= 0;
};

const matchesRegex = (left, right) => {
  if (isEmptyValue(left) || isNil(right) || right === "") return false;
  try {
    return new RegExp(String(right)).test(String(left));
  } catch (_error) {
    return false;
  }
};

const toFiniteNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string" || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Compares numerically only when both sides are numbers, so a form value typed
 * as "9" is not ordered as a string against 12. Anything else — ISO dates above
 * all — keeps the lexicographic comparison it already relied on.
 */
const ordering = (compare) => (left, right) => {
  const leftNumber = toFiniteNumber(left);
  const rightNumber = toFiniteNumber(right);
  if (leftNumber !== null && rightNumber !== null) {
    return compare(leftNumber, rightNumber);
  }
  return compare(left, right);
};

const operations = {
  // Coerce to string: form values are usually strings while options may be numbers.
  identity: (left, right) => String(left) === String(right),
  not_identity: (left, right) => String(left) !== String(right),
  isEmpty: (left, right) =>
    isEmptyValue(left) === (isNil(right) ? true : right),
  not_empty: (left) => !isEmptyValue(left),
  includes: includesValue,
  regex: matchesRegex,
  lt: ordering((left, right) => left < right),
  gt: ordering((left, right) => left > right),
  lte: ordering((left, right) => left <= right),
  gte: ordering((left, right) => left >= right),
};

export const evaluateLeafCondition = (condition, context, now = new Date()) => {
  if (isNil(condition) || isNil(getPath(condition, "leftOperand"))) return true;
  const operation = operations[condition.operation] || operations.identity;
  return (
    operation(
      toCalendarDateKey(leftValue(condition, context)),
      toCalendarDateKey(resolveRightOperand(condition.rightOperand, now)),
    ) === true
  );
};

/**
 * Supports:
 * - flat array of leaf conditions (AND) — legacy
 * - { all: [...] } / { any: [...] } / { not: node }
 * - a single leaf condition object
 */
export const evaluateConditionNode = (node, context, now = new Date()) => {
  if (isNil(node)) return true;
  if (Array.isArray(node)) {
    if (!node.length) return true;
    return node.every((item) => evaluateConditionNode(item, context, now));
  }
  if (node.all) {
    return (node.all || []).every((item) =>
      evaluateConditionNode(item, context, now),
    );
  }
  if (node.any) {
    const items = node.any || [];
    if (!items.length) return true;
    return items.some((item) => evaluateConditionNode(item, context, now));
  }
  if (!isNil(node.not)) {
    return !evaluateConditionNode(node.not, context, now);
  }
  return evaluateLeafCondition(node, context, now);
};

export const evaluateConditions = (conditions, context, now = new Date()) =>
  evaluateConditionNode(conditions, context, now);

export const isFieldVisible = (field, context, now = new Date()) =>
  evaluateConditions((field && field.visibleWhen) || [], context, now);
